import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations, SUPPORTED_LANGUAGES } from "@/lib/translations";

const LanguageContext = createContext({
  lang: "pt",
  setLang: () => {},
  t: translations.pt,
});

export const LanguageProvider = ({ children }) => {
  // Start with the default language on BOTH react-snap prerender AND client
  // hydration, so the initial React tree matches the pre-rendered DOM
  // exactly. We then read the user's saved preference in useEffect (after
  // mount) and swap. Prevents React #418 hydration mismatch when the user
  // has a different saved lang than the default.
  const [lang, setLang] = useState("pt");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem("npdl-lang");
    if (saved && SUPPORTED_LANGUAGES.includes(saved) && saved !== lang) {
      setLang(saved);
    }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && hydrated) {
      window.localStorage.setItem("npdl-lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang, hydrated]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: translations[lang] || translations.pt,
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
